import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { collection, addDoc, serverTimestamp, onSnapshot, query, orderBy, getDocs, doc, updateDoc, deleteDoc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { User, UserManagementUser } from '../types';
import { getApiUrl } from '../config';
import { AddUserModal } from './AddUserModal';
import { calculateCaseFormCompletion, getCompletionColor, calculateMeetingCompletion, countCaseMeetings } from '../utils/intakeCompletion';
import { canSeeSitemap, isSuperAdmin, hasAnyOrgRole } from '../utils/roleUtils';

interface Group {
  id: string;
  name: string;
  description: string;
  logoUrl?: string;
  managers: string[]; // Array of user IDs
  managerNames: string[]; // Array of user names for display
  organizationId?: string; // Organization ID this group belongs to
  organizationName?: string; // Organization name for display
  programId?: string; // Program ID this group belongs to
  programName?: string; // Program name for display
  maxMembers?: number;
  category: string; // e.g., 'therapy', 'support', 'training', 'social'
  status: 'active' | 'inactive' | 'full' | 'completed';
  meetingSchedule?: string;
  location?: string;
  createdBy: string;
  createdByEmail: string;
  createdAt: any;
}

interface Organization {
  id: string;
  name: string;
  description: string;
  logoUrl?: string;
  administrators: string[];
  administratorNames: string[];
  createdBy: string;
  createdByEmail: string;
  createdAt: any;
  requiredMeetings?: number; // Number of meetings required to complete the process
}

interface Program {
  id: string;
  name: string;
  description: string;
  logoUrl?: string;
  managers: string[];
  managerNames: string[];
  organizationId?: string;
  organizationName?: string;
  createdBy: string;
  createdByEmail: string;
  createdAt: any;
}

interface GroupsProps {
  user: User;
}

type WizardStep = 'basic' | 'managers' | 'details';

export default function Groups({ user }: GroupsProps) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [groups, setGroups] = useState<Group[]>([]);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [programs, setPrograms] = useState<Program[]>([]);
  const [filteredPrograms, setFilteredPrograms] = useState<Program[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [showWizard, setShowWizard] = useState(false);
  const [currentStep, setCurrentStep] = useState<WizardStep>('basic');
  const [isUploading, setIsUploading] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<Group | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [detailsViewMode, setDetailsViewMode] = useState<'view' | 'edit'>('view');
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [allUsers, setAllUsers] = useState<UserManagementUser[]>([]);
  const [groupCompletions, setGroupCompletions] = useState<{ [groupId: string]: number }>({});
  const [groupMeetingCompletions, setGroupMeetingCompletions] = useState<{ [groupId: string]: { completed: number; total: number } }>({});
  
  // Form data
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    logoUrl: '',
    managers: [] as string[],
    managerNames: [] as string[],
    organizationId: '',
    organizationName: '',
    programId: '',
    programName: '',
    maxMembers: 10,
    category: 'therapy' as string,
    status: 'active' as Group['status'],
    meetingSchedule: '',
    location: ''
  });

  // User search for wizard
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<UserManagementUser[]>([]);
  const [isSearching, setIsSearching] = useState(false);

  // User search for edit modal
  const [detailsUserSearchTerm, setDetailsUserSearchTerm] = useState('');
  const [detailsSearchResults, setDetailsSearchResults] = useState<UserManagementUser[]>([]);
  const [isDetailsSearching, setIsDetailsSearching] = useState(false);

  // Edit modal data
  const [editData, setEditData] = useState({
    name: '',
    description: '',
    logoUrl: '',
    managers: [] as string[],
    managerNames: [] as string[],
    maxMembers: 10,
    category: 'therapy',
    status: 'active' as Group['status'],
    meetingSchedule: '',
    location: ''
  });

  // Load organizations
  useEffect(() => {
    const q = query(collection(db, 'organizations'), orderBy('createdAt', 'desc'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const orgsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Organization[];
      setOrganizations(orgsData);
    });

    return () => unsubscribe();
  }, []);

  // Load programs
  useEffect(() => {
    const q = query(collection(db, 'programs'), orderBy('createdAt', 'desc'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const programsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Program[];
      setPrograms(programsData);
    });

    return () => unsubscribe();
  }, []);

  // Load all users for member display
  useEffect(() => {
    const loadUsers = async () => {
      try {
        const usersRef = collection(db, 'users');
        const snapshot = await getDocs(usersRef);
        const users: UserManagementUser[] = [];
        
        snapshot.forEach((doc) => {
          const userData = doc.data();
          if (!userData.deleted) {
            users.push({
              id: doc.id,
              name: userData.name || '',
              email: userData.email || '',
              role: userData.role,
              organizationId: userData.organizationId,
              organizationRole: userData.organizationRole,
              groupId: userData.groupId,
              programId: userData.programId,
              createdAt: userData.createdAt?.toDate?.()?.toISOString() || '',
              lastLoginAt: userData.lastLoginAt?.toDate?.()?.toISOString() || '',
              loginCount: userData.loginCount || 0,
              blocked: userData.blocked || false
            });
          }
        });
        setAllUsers(users);
      } catch (error) {
        console.error('Error loading users:', error);
      }
    };
    loadUsers();
  }, []);

  // Calculate group completion statistics
  useEffect(() => {
    const calculateGroupCompletions = async () => {
      if (groups.length === 0 || allUsers.length === 0) {
        setGroupCompletions({});
        return;
      }

      try {
        // Load all patients
        const { getAllPatients } = await import('../firebase');
        const allPatients = await getAllPatients();
        const activePatients = allPatients.filter(p => p.status === 'active');

        if (activePatients.length === 0) {
          setGroupCompletions({});
          return;
        }

        // Import form management functions
        const { getAllForms, getCaseSubmissions } = await import('../firebase/formManagement');
        const allForms = await getAllForms();

        const completions: { [groupId: string]: number } = {};
        const meetingCompletions: { [groupId: string]: { completed: number; total: number } } = {};

        // Calculate completion for each group
        for (const group of groups) {
          // Get all members of this group
          const groupMembers = allUsers.filter(u => u.groupId === group.id);
          
          if (groupMembers.length === 0) {
            completions[group.id] = 0;
            meetingCompletions[group.id] = { completed: 0, total: 0 };
            continue;
          }

          // Get required meetings from parent organization
          const parentOrg = group.organizationId ? organizations.find(o => o.id === group.organizationId) : null;
          const requiredMeetings = parentOrg?.requiredMeetings || 0;

          // Collect all intake completions and count meetings
          const allIntakeCompletions: number[] = [];
          let totalCompletedMeetings = 0;
          let totalRequiredMeetings = 0;

          // Get all active cases belonging to this group (regardless of assignment)
          const groupCases = activePatients.filter(p => p.groupId === group.id);

          // Calculate intake and count meetings for all cases in this group
          for (const patient of groupCases) {
            try {
              // Calculate intake completion (only for cases assigned to group members)
              const isAssignedToGroupMember = groupMembers.some(member => 
                Array.isArray(patient.assignedAdmins) && patient.assignedAdmins.includes(member.id)
              );
              
              if (isAssignedToGroupMember) {
                const intakeCompletion = await calculateCaseFormCompletion(patient.caseId);
                // Include all cases, even if completion is 0%
                allIntakeCompletions.push(intakeCompletion);
              }
              
              // Count meetings: completed and total from events DB (for all cases in group)
              const meetingCounts = await countCaseMeetings(patient.caseId);
              totalCompletedMeetings += meetingCounts.completed;
              totalRequiredMeetings += meetingCounts.total;
            } catch (error) {
              console.error(`Error calculating completion for case ${patient.caseId}:`, error);
            }
          }

          // Calculate average intake completion
          if (allIntakeCompletions.length === 0) {
            completions[group.id] = 0;
          } else {
            const totalIntakeCompletion = allIntakeCompletions.reduce((sum, comp) => sum + comp, 0);
            const averageIntakeCompletion = Math.round(totalIntakeCompletion / allIntakeCompletions.length);
            completions[group.id] = averageIntakeCompletion;
          }

          // Set meeting count as completed/all
          meetingCompletions[group.id] = totalRequiredMeetings > 0 
            ? { completed: totalCompletedMeetings, total: totalRequiredMeetings }
            : { completed: 0, total: 0 };
        }

        setGroupCompletions(completions);
        setGroupMeetingCompletions(meetingCompletions);
      } catch (error) {
        console.error('Error calculating group completions:', error);
        setGroupCompletions({});
        setGroupMeetingCompletions({});
      }
    };

    calculateGroupCompletions();
  }, [groups, allUsers, organizations]);

  // Filter programs based on selected organization
  useEffect(() => {
    if (formData.organizationId) {
      const filtered = programs.filter(p => p.organizationId === formData.organizationId);
      setFilteredPrograms(filtered);
    } else {
      setFilteredPrograms([]);
    }
  }, [formData.organizationId, programs]);

  // Load groups
  useEffect(() => {
    const loadGroups = async () => {
      if (!user) return;
      
      // Load user's groupId from Firestore if team_manager
      let userGroupId: string | undefined;
      const isTeamManager = user?.organizationRole === 'team_manager';
      if (isTeamManager) {
        try {
          const userDoc = await getDoc(doc(db, 'users', user.id));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            userGroupId = userData.groupId;
          }
        } catch (error) {
          console.error('Error loading user groupId:', error);
        }
      }
      
      const q = query(collection(db, 'groups'), orderBy('createdAt', 'desc'));
      const unsubscribe = onSnapshot(q, (snapshot) => {
        let groupsData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as Group[];
        
        // Filter groups based on user role
        // Super admin and program managers see all groups
        // Team managers only see groups where they are managers or where their groupId matches
        if (!isSuperAdmin(user) && !hasAnyOrgRole(user, ['admin', 'program_manager', 'department_manager'])) {
          if (isTeamManager) {
            // Team manager sees only groups where they are a manager or where their groupId matches
            groupsData = groupsData.filter(group => 
              group.managers?.includes(user.id) || (userGroupId && group.id === userGroupId)
            );
          }
        }
        
        setGroups(groupsData);
        setIsLoading(false);
      });

      return () => unsubscribe();
    };
    
    const unsubscribePromise = loadGroups();
    
    return () => {
      unsubscribePromise.then(unsubscribe => {
        if (unsubscribe) unsubscribe();
      });
    };
  }, [user]);

  // Search users
  const searchUsers = async (searchTerm: string) => {
    if (searchTerm.length < 2) {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    try {
      const usersRef = collection(db, 'users');
      const snapshot = await getDocs(usersRef);
      const users = snapshot.docs
        .map(doc => ({
        id: doc.id,
        ...doc.data()
        }))
        .filter((user: any) => !user.deleted) as UserManagementUser[];

      const filtered = users.filter(user => 
        user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email.toLowerCase().includes(searchTerm.toLowerCase())
      );

      setSearchResults(filtered);
    } catch (error) {
      console.error('Error searching users:', error);
    } finally {
      setIsSearching(false);
    }
  };

  // Handle user search
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      searchUsers(userSearchTerm);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [userSearchTerm]);

  // Search users for edit modal
  const searchDetailsUsers = async (searchTerm: string) => {
    if (searchTerm.length < 2) {
      setDetailsSearchResults([]);
      return;
    }

    setIsDetailsSearching(true);
    try {
      const usersRef = collection(db, 'users');
      const snapshot = await getDocs(usersRef);
      const users: UserManagementUser[] = [];
      
      snapshot.forEach((doc) => {
        const userData = doc.data();
        if (!userData.deleted) {
          const user: UserManagementUser = {
            id: doc.id,
            name: userData.name || '',
            email: userData.email || '',
            role: userData.role || '',
            createdAt: userData.createdAt?.toDate?.()?.toISOString() || '',
            lastLoginAt: userData.lastLoginAt?.toDate?.()?.toISOString() || '',
            loginCount: userData.loginCount || 0,
            blocked: userData.blocked || false
          };
          
          // Filter by search term and exclude already assigned users
          if ((user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
              user.email.toLowerCase().includes(searchTerm.toLowerCase())) &&
              !editData.managers.includes(user.id)) {
            users.push(user);
          }
        }
      });
      
      setDetailsSearchResults(users);
    } catch (error) {
      console.error('Error searching users:', error);
    } finally {
      setIsDetailsSearching(false);
    }
  };

  // Handle user search for edit modal
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      searchDetailsUsers(detailsUserSearchTerm);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [detailsUserSearchTerm, editData.managers]);

  const handleAddManager = (user: UserManagementUser) => {
    if (!formData.managers.includes(user.id)) {
      setFormData(prev => ({
        ...prev,
        managers: [...prev.managers, user.id],
        managerNames: [...prev.managerNames, user.name]
      }));
    }
    setUserSearchTerm('');
    setSearchResults([]);
  };

  const handleRemoveManager = (userId: string) => {
    const index = formData.managers.indexOf(userId);
    if (index > -1) {
      setFormData(prev => ({
        ...prev,
        managers: prev.managers.filter(id => id !== userId),
        managerNames: prev.managerNames.filter((_, i) => i !== index)
      }));
    }
  };

  // Add manager to edit modal
  const handleAddDetailsManager = async (user: UserManagementUser) => {
    if (!editData.managers.includes(user.id)) {
      setEditData(prev => ({
        ...prev,
        managers: [...prev.managers, user.id],
        managerNames: [...prev.managerNames, user.name]
      }));

      // Set groupId for the manager so they appear as a member
      if (selectedGroup) {
        try {
          const userRef = doc(db, 'users', user.id);
          await updateDoc(userRef, {
            groupId: selectedGroup.id
          });
        } catch (error) {
          console.error(`Error setting groupId for manager ${user.id}:`, error);
        }
      }
    }
    setDetailsUserSearchTerm('');
    setDetailsSearchResults([]);
  };

  // Remove manager from edit modal
  const handleRemoveDetailsManager = async (userId: string) => {
    if (!selectedGroup) return;
    
    try {
      // Remove from group's managers array
      const groupRef = doc(db, 'groups', selectedGroup.id);
      const groupDoc = await getDoc(groupRef);
      if (groupDoc.exists()) {
        const groupData = groupDoc.data();
        const currentManagers = groupData.managers || [];
        const currentManagerNames = groupData.managerNames || [];
        const index = currentManagers.indexOf(userId);
        
        if (index > -1) {
          await updateDoc(groupRef, {
            managers: currentManagers.filter((id: string) => id !== userId),
            managerNames: currentManagerNames.filter((_: string, i: number) => i !== index)
          });
        }
      }
      
      // Update local state
      const index = editData.managers.indexOf(userId);
      if (index > -1) {
        setEditData(prev => ({
          ...prev,
          managers: prev.managers.filter(id => id !== userId),
          managerNames: prev.managerNames.filter((_, i) => i !== index)
        }));
      }
    } catch (error) {
      console.error('Error removing manager:', error);
    }
  };

  const handleRemoveMember = async (userId: string) => {
    if (!selectedGroup) return;
    
    try {
      // Remove user's groupId (remove from group)
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        groupId: null
      });
      
      // If user was also a manager, remove from managers array
      if (editData.managers.includes(userId)) {
        await handleRemoveDetailsManager(userId);
      }
      
      // Reload users to refresh the list
      const usersRef = collection(db, 'users');
      const snapshot = await getDocs(usersRef);
      const users: UserManagementUser[] = [];
      
      snapshot.forEach((doc) => {
        const userData = doc.data();
        if (!userData.deleted) {
          users.push({
            id: doc.id,
            name: userData.name || '',
            email: userData.email || '',
            role: userData.role,
            organizationId: userData.organizationId,
            organizationRole: userData.organizationRole,
            groupId: userData.groupId,
            programId: userData.programId,
            createdAt: userData.createdAt?.toDate?.()?.toISOString() || '',
            lastLoginAt: userData.lastLoginAt?.toDate?.()?.toISOString() || '',
            loginCount: userData.loginCount || 0,
            blocked: userData.blocked || false
          });
        }
      });
      setAllUsers(users);
    } catch (error) {
      console.error('Error removing member:', error);
    }
  };


  // Logo upload handler
  const handleLogoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setIsUploading(true);
    try {
      // Validate and resize image to max 1000x1000 JPG
      const { validateAndResizeIcon } = await import('../utils');
      const resizedFile = await validateAndResizeIcon(file);

      const formDataUpload = new FormData();
      formDataUpload.append('file', resizedFile);

      const response = await fetch(getApiUrl('/upload'), {
        method: 'POST',
        body: formDataUpload,
      });

      if (response.ok) {
        const result = await response.json();
        // Add cache-busting parameter to force image reload
        const imageUrl = result.file_url + (result.file_url.includes('?') ? '&' : '?') + 't=' + Date.now();
        setFormData(prev => ({ ...prev, logoUrl: imageUrl }));
      } else {
        throw new Error('Upload failed');
      }
    } catch (error: any) {
      console.error('Error uploading logo:', error);
      alert(error.message || 'Failed to upload icon. Please ensure the image is JPG format and try again.');
    } finally {
      setIsUploading(false);
      // Reset file input to allow selecting the same file again
      if (event.target) {
        event.target.value = '';
      }
    }
  };

  // Create group
  const handleCreateGroup = async () => {
    if (!formData.name.trim() || !formData.organizationId || !formData.programId) {
      return;
    }

    setIsCreating(true);
    try {
      const groupRef = await addDoc(collection(db, 'groups'), {
        name: formData.name.trim(),
        description: formData.description.trim(),
        logoUrl: formData.logoUrl,
        managers: formData.managers,
        managerNames: formData.managerNames,
        organizationId: formData.organizationId,
        organizationName: formData.organizationName,
        programId: formData.programId,
        programName: formData.programName,
        createdBy: user.name,
        createdByEmail: user.email,
        createdAt: serverTimestamp()
      });

      // Set groupId for all managers so they appear as members
      for (const managerId of formData.managers) {
        try {
          const userRef = doc(db, 'users', managerId);
          await updateDoc(userRef, {
            groupId: groupRef.id
          });
        } catch (error) {
          console.error(`Error setting groupId for manager ${managerId}:`, error);
        }
      }

      // Reset form
      setFormData({
        name: '',
        description: '',
        logoUrl: '',
        managers: [],
        managerNames: [],
        organizationId: '',
        organizationName: '',
        programId: '',
        programName: '',
        maxMembers: 10,
        category: 'therapy',
        status: 'active',
        meetingSchedule: '',
        location: ''
      });
      setShowWizard(false);
      setCurrentStep('basic');
      // Group created successfully
    } catch (error) {
      console.error('Error creating group:', error);
      console.error('Create error');
    } finally {
      setIsCreating(false);
    }
  };

  // Logo upload handler for edit modal
  const handleDetailsLogoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setIsUploading(true);
    try {
      // Validate and resize image to max 1000x1000 JPG
      const { validateAndResizeIcon } = await import('../utils');
      const resizedFile = await validateAndResizeIcon(file);

      const formDataUpload = new FormData();
      formDataUpload.append('file', resizedFile);

      const response = await fetch(getApiUrl('/upload'), {
        method: 'POST',
        body: formDataUpload,
      });

      if (response.ok) {
        const result = await response.json();
        // Add cache-busting parameter to force image reload
        const imageUrl = result.file_url + (result.file_url.includes('?') ? '&' : '?') + 't=' + Date.now();
        setEditData(prev => ({ ...prev, logoUrl: imageUrl }));
      } else {
        throw new Error('Upload failed');
      }
    } catch (error: any) {
      console.error('Error uploading logo:', error);
      alert(error.message || 'Failed to upload icon. Please ensure the image is JPG format and try again.');
    } finally {
      setIsUploading(false);
      // Reset file input to allow selecting the same file again
      if (event.target) {
        event.target.value = '';
      }
    }
  };

  // Update group
  const handleUpdateGroup = async () => {
    if (!selectedGroup || !editData.name.trim()) return;

    setIsUpdating(true);
    try {
      const groupRef = doc(db, 'groups', selectedGroup.id);
      await updateDoc(groupRef, {
        name: editData.name,
        description: editData.description,
        logoUrl: editData.logoUrl,
        managers: editData.managers,
        managerNames: editData.managerNames
      });

      // Set groupId for all managers so they appear as members
      for (const managerId of editData.managers) {
        try {
          const userRef = doc(db, 'users', managerId);
          await updateDoc(userRef, {
            groupId: selectedGroup.id
          });
        } catch (error) {
          console.error(`Error setting groupId for manager ${managerId}:`, error);
        }
      }

      setShowDetailsModal(false);
      // Group updated successfully
    } catch (error) {
      console.error('Error updating group:', error);
      console.error('Update error');
    } finally {
      setIsUpdating(false);
    }
  };

  // Delete group
  const handleDeleteGroup = async (groupId: string) => {
    // Delete group

    setIsDeleting(true);
    try {
      await deleteDoc(doc(db, 'groups', groupId));
      // Group deleted successfully - close modal and reset selected group
      setShowDetailsModal(false);
      setSelectedGroup(null);
    } catch (error) {
      console.error('Error deleting group:', error);
      console.error('Delete error');
    } finally {
      setIsDeleting(false);
    }
  };

  // Open details modal
  const handleOpenDetails = (group: Group) => {
    setSelectedGroup(group);
    setEditData({
      name: group.name,
      description: group.description || '',
      logoUrl: group.logoUrl || '',
      managers: group.managers || [],
      managerNames: group.managerNames || [],
      maxMembers: group.maxMembers || 10,
      category: group.category || 'therapy',
      status: group.status,
      meetingSchedule: group.meetingSchedule || '',
      location: group.location || ''
    });
    setDetailsViewMode('view');
    setDetailsUserSearchTerm('');
    setDetailsSearchResults([]);
    setShowDetailsModal(true);
  };


  if (isLoading) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <div style={{ fontSize: '18px', color: '#666' }}>
          {t('groups.loading')}...
        </div>
      </div>
    );
  }

  return (
    <div className="groups-container" style={{ padding: '20px' }}>
      {/* Header */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: '30px'
      }}>
        <div>
          <h2 style={{ margin: 0, fontSize: '28px', fontWeight: 600, color: '#333' }}>
            {t('groups.title')}
          </h2>
          <p style={{ margin: '5px 0 0 0', color: '#666', fontSize: '16px' }}>
            {t('groups.subtitle')}
          </p>
        </div>
        <button
          onClick={() => setShowWizard(true)}
          style={{
            background: '#007acc',
            color: 'white',
            border: 'none',
            padding: '12px 24px',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '16px',
            fontWeight: '500',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          ➕ {t('groups.createNew')}
        </button>
      </div>

      {/* Groups Grid */}
      {groups.length === 0 ? (
        <div style={{
          textAlign: 'center',
          padding: '60px 20px',
          color: '#666'
        }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>👥</div>
          <h3 style={{ margin: '0 0 8px 0', fontSize: '20px' }}>
            {t('groups.noGroups')}
          </h3>
          <p style={{ margin: 0, fontSize: '14px', color: '#999' }}>
            {t('groups.createFirstGroup')}
          </p>
        </div>
      ) : (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
          gap: '20px'
        }}>
          {groups.map((group) => {
            const isRTL = i18n.language === 'he';
            return (
              <div
                key={group.id}
                onClick={() => handleOpenDetails(group)}
                style={{
                  backgroundColor: '#ffffff',
                  border: '1px solid #e0e0e0',
                  borderRadius: '12px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer',
                  position: 'relative',
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: isRTL ? 'row-reverse' : 'row',
                  height: '100%'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.12)';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.backgroundColor = '#f8f9fa';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.08)';
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.backgroundColor = '#ffffff';
                }}
              >
              {/* Content Section */}
              <div style={{ padding: '24px', flex: 1, display: 'flex', flexDirection: 'column' }}>
                <h3 style={{ 
                    margin: '0 0 10px 0', 
                    color: '#1e293b',
                    fontSize: '20px',
                    fontWeight: '600',
                    lineHeight: '1.3'
                }}>
                  {group.name}
                </h3>
                {group.description && (
                  <p style={{
                      margin: '0 0 14px 0',
                      color: '#64748b',
                    fontSize: '14px',
                      lineHeight: '1.5',
                      flex: 1
                  }}>
                    {group.description}
                  </p>
                )}

                {/* Completion Statistics */}
                <div style={{
                  marginTop: 'auto',
                  paddingTop: '12px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px'
                }}>
                  {/* Intake Completion */}
                  {groupCompletions[group.id] !== undefined && (
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      <span style={{ fontSize: '11px', color: '#666', minWidth: '50px' }}>
                        {i18n.language === 'he' ? 'קליטה:' : 'Intake:'}
                      </span>
                      <div style={{
                        width: '60px',
                        height: '6px',
                        backgroundColor: '#e0e0e0',
                        borderRadius: '3px',
                        overflow: 'hidden',
                        position: 'relative'
                      }}>
                        <div
                          style={{
                            width: `${groupCompletions[group.id]}%`,
                            height: '100%',
                            backgroundColor: getCompletionColor(groupCompletions[group.id]),
                            transition: 'width 0.3s ease'
                          }}
                        />
                      </div>
                      <span style={{
                        fontSize: '12px',
                        fontWeight: '600',
                        color: getCompletionColor(groupCompletions[group.id]),
                        minWidth: '35px'
                      }}>
                        {groupCompletions[group.id]}%
                      </span>
                    </div>
                  )}
                </div>
            
              </div>
              
              {/* Image Section - Right side */}
              <div style={{
                width: '200px',
                minWidth: '200px',
                height: '100%',
                backgroundColor: '#f3f4f6',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden',
                flexShrink: 0
              }}>
                {group.logoUrl ? (
                  <img
                    src={group.logoUrl}
                    alt={`${group.name} logo`}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover'
                    }}
                  />
                ) : (
                  <div style={{
                    fontSize: '64px',
                    color: '#6b7280'
                  }}>
                    👥
                  </div>
                )}
              </div>
            </div>
            );
          })}
        </div>
      )}

      {/* Create Group Wizard Modal */}
      {showWizard && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '30px',
            width: '90%',
            maxWidth: '600px',
            maxHeight: '90vh',
            overflow: 'auto',
            position: 'relative'
          }}>
            {/* Close X Button */}
            <button
              onClick={() => {
                setShowWizard(false);
                setCurrentStep('basic');
                setFormData({
                  name: '',
                  description: '',
                  logoUrl: '',
                  managers: [],
                  managerNames: [],
                  organizationId: '',
                  organizationName: '',
                  programId: '',
                  programName: '',
                  maxMembers: 10,
                  category: 'therapy',
                  status: 'active',
                  meetingSchedule: '',
                  location: ''
                });
              }}
              style={{
                position: 'absolute',
                top: '15px',
                ...(i18n.language === 'he' ? { left: '15px' } : { right: '15px' }),
                background: 'none',
                border: 'none',
                fontSize: '28px',
                color: '#666',
                cursor: 'pointer',
                width: '30px',
                height: '30px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '50%',
                transition: 'all 0.2s ease',
                padding: 0,
                lineHeight: 1
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#f0f0f0';
                e.currentTarget.style.color = '#000';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.color = '#666';
              }}
              title={t('groups.close') || 'Close'}
            >
              ×
            </button>
            <h3 style={{ margin: '0 0 20px 0', fontSize: '24px' }}>
              {t('groups.createNew')}
            </h3>

            {/* Step Indicator */}
            <div style={{ display: 'flex', marginBottom: '30px', gap: '10px' }}>
              {(['basic', 'managers', 'details'] as WizardStep[]).map((step, index) => (
                <div
                  key={step}
                  style={{
                    flex: 1,
                    height: '4px',
                    backgroundColor: index <= (['basic', 'managers', 'details'] as WizardStep[]).indexOf(currentStep) ? '#007acc' : '#e0e0e0',
                    borderRadius: '2px'
                  }}
                />
              ))}
            </div>

            {/* Step Content */}
            {currentStep === 'basic' && (
              <div>
                <h4>{t('groups.wizard.basicInfo')}</h4>
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                    {t('groups.organization')} *
                  </label>
                  <select
                    value={formData.organizationId}
                    onChange={(e) => {
                      const selectedOrg = organizations.find(org => org.id === e.target.value);
                      setFormData(prev => ({ 
                        ...prev, 
                        organizationId: e.target.value,
                        organizationName: selectedOrg?.name || '',
                        programId: '',
                        programName: ''
                      }));
                    }}
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '1px solid #ddd',
                      borderRadius: '6px',
                      fontSize: '16px'
                    }}
                  >
                    <option value="">{t('groups.selectOrganization')}</option>
                    {organizations.map(org => (
                      <option key={org.id} value={org.id}>{org.name}</option>
                    ))}
                  </select>
                </div>
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                    {t('groups.program')} *
                  </label>
                  <select
                    value={formData.programId}
                    onChange={(e) => {
                      const selectedProgram = filteredPrograms.find(p => p.id === e.target.value);
                      setFormData(prev => ({ 
                        ...prev, 
                        programId: e.target.value,
                        programName: selectedProgram?.name || ''
                      }));
                    }}
                    disabled={!formData.organizationId}
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '1px solid #ddd',
                      borderRadius: '6px',
                      fontSize: '16px',
                      opacity: !formData.organizationId ? 0.6 : 1
                    }}
                  >
                    <option value="">{t('groups.selectProgram')}</option>
                    {filteredPrograms.map(program => (
                      <option key={program.id} value={program.id}>{program.name}</option>
                    ))}
                  </select>
                </div>
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                    {t('groups.name')} *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '1px solid #ddd',
                      borderRadius: '6px',
                      fontSize: '16px'
                    }}
                    placeholder={t('groups.enterName')}
                  />
                </div>
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                    {t('groups.description')}
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '1px solid #ddd',
                      borderRadius: '6px',
                      fontSize: '16px',
                      minHeight: '100px',
                      resize: 'vertical'
                    }}
                    placeholder={t('groups.enterDescription')}
                  />
                </div>
              </div>
            )}

            {currentStep === 'managers' && (
              <div>
                <h4>{t('groups.wizard.managers')}</h4>
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                    {t('groups.searchManagers')}
                  </label>
                  <input
                    type="text"
                    value={userSearchTerm}
                    onChange={(e) => setUserSearchTerm(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '1px solid #ddd',
                      borderRadius: '6px',
                      fontSize: '16px'
                    }}
                    placeholder={t('groups.searchUsersPlaceholder')}
                  />
                  
                  {/* Search Results */}
                  {searchResults.length > 0 && (
                    <div style={{
                      border: '1px solid #ddd',
                      borderTop: 'none',
                      borderRadius: '0 0 6px 6px',
                      maxHeight: '200px',
                      overflow: 'auto',
                      backgroundColor: 'white'
                    }}>
                      {searchResults.map(user => (
                        <div
                          key={user.id}
                          onClick={() => handleAddManager(user)}
                          style={{
                            padding: '12px',
                            cursor: 'pointer',
                            borderBottom: '1px solid #f0f0f0',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#f8f9fa';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'white';
                          }}
                        >
                          <div>
                            <div style={{ fontWeight: '500' }}>{user.name}</div>
                            <div style={{ fontSize: '14px', color: '#666' }}>{user.email}</div>
                          </div>
                          {formData.managers.includes(user.id) && (
                            <span style={{ color: '#28a745', fontSize: '14px' }}>✓ {t('groups.added')}</span>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Selected Managers */}
                {formData.managerNames.length > 0 && (
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                      {t('groups.selectedManagers')}
                    </label>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                      {formData.managerNames.map((name, index) => (
                        <div
                          key={formData.managers[index]}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            padding: '6px 12px',
                            backgroundColor: '#e3f2fd',
                            borderRadius: '20px',
                            fontSize: '14px'
                          }}
                        >
                          {name}
                          <button
                            onClick={() => handleRemoveManager(formData.managers[index])}
                            style={{
                              background: 'none',
                              border: 'none',
                              cursor: 'pointer',
                              color: '#666',
                              fontSize: '16px',
                              padding: 0
                            }}
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}


            {currentStep === 'details' && (
              <div>
                <h4>{t('groups.wizard.details')}</h4>
                
                {/* Logo Upload */}
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                    {t('groups.logo')}
                  </label>
                  <input
                    type="file"
                    accept="image/jpeg,image/jpg,.jpg,.jpeg"
                    onChange={handleLogoUpload}
                    disabled={isUploading}
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '1px solid #ddd',
                      borderRadius: '6px'
                    }}
                  />
                  {isUploading && (
                    <div style={{ marginTop: '8px', color: '#007acc' }}>
                      {t('groups.wizard.uploading')}...
                    </div>
                  )}
                  {formData.logoUrl && (
                    <img
                      key={formData.logoUrl}
                      src={formData.logoUrl}
                      alt="Group logo"
                      style={{
                        marginTop: '12px',
                        width: '100px',
                        height: '100px',
                        objectFit: 'cover',
                        borderRadius: '8px',
                        border: '1px solid #ddd'
                      }}
                      onLoad={() => setIsUploading(false)}
                    />
                  )}
                </div>

              </div>
            )}

            {/* Navigation Buttons */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '30px' }}>
              <div style={{ display: 'flex', gap: '12px' }}>
                {currentStep !== 'basic' && (
                  <button
                    onClick={() => {
                      const steps: WizardStep[] = ['basic', 'managers', 'details'];
                      const currentIndex = steps.indexOf(currentStep);
                      if (currentIndex > 0) {
                        setCurrentStep(steps[currentIndex - 1]);
                      }
                    }}
                    style={{
                      padding: '12px 24px',
                      border: '1px solid #ddd',
                      borderRadius: '6px',
                      backgroundColor: 'white',
                      cursor: 'pointer',
                      fontSize: '16px'
                    }}
                  >
                    {t('groups.wizard.back')}
                  </button>
                )}
                {currentStep === 'details' ? (
                  <button
                    onClick={handleCreateGroup}
                    disabled={isCreating || !formData.name.trim() || !formData.organizationId || !formData.programId}
                    style={{
                      padding: '12px 24px',
                      border: 'none',
                      borderRadius: '6px',
                      backgroundColor: '#007acc',
                      color: 'white',
                      cursor: isCreating || !formData.name.trim() || !formData.organizationId || !formData.programId ? 'not-allowed' : 'pointer',
                      fontSize: '16px',
                      opacity: isCreating || !formData.name.trim() || !formData.organizationId || !formData.programId ? 0.6 : 1
                    }}
                  >
                    {isCreating ? t('groups.creating') : t('groups.create')}
                  </button>
                ) : (
                  <button
                    onClick={() => {
                      const steps: WizardStep[] = ['basic', 'managers', 'details'];
                      const currentIndex = steps.indexOf(currentStep);
                      if (currentIndex < steps.length - 1) {
                        setCurrentStep(steps[currentIndex + 1]);
                      }
                    }}
                    disabled={currentStep === 'basic' && (!formData.name.trim() || !formData.organizationId || !formData.programId)}
                    style={{
                      padding: '12px 24px',
                      border: 'none',
                      borderRadius: '6px',
                      backgroundColor: '#007acc',
                      color: 'white',
                      cursor: (currentStep === 'basic' && (!formData.name.trim() || !formData.organizationId || !formData.programId)) ? 'not-allowed' : 'pointer',
                      fontSize: '16px',
                      opacity: (currentStep === 'basic' && (!formData.name.trim() || !formData.organizationId || !formData.programId)) ? 0.6 : 1
                    }}
                  >
                    {t('groups.wizard.next')}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Group Details Modal */}
      {showDetailsModal && selectedGroup && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '30px',
            width: '90%',
            maxWidth: '800px',
            maxHeight: '90vh',
            overflow: 'auto',
            position: 'relative'
          }}>
            {/* Close X Button */}
              <button
                onClick={() => setShowDetailsModal(false)}
                style={{
                position: 'absolute',
                top: '15px',
                ...(i18n.language === 'he' ? { right: '15px' } : { left: '15px' }),
                  background: 'none',
                  border: 'none',
                  fontSize: '28px',
                  color: '#666',
                  cursor: 'pointer',
                  width: '30px',
                  height: '30px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '50%',
                  transition: 'all 0.2s ease',
                  padding: 0,
                lineHeight: 1
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#f0f0f0';
                  e.currentTarget.style.color = '#000';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.color = '#666';
                }}
                title={t('groups.close') || 'Close'}
              >
                ×
              </button>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', paddingRight: '40px' }}>
              <h3 style={{ margin: 0, fontSize: '24px' }}>
                {selectedGroup.name}
              </h3>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                {detailsViewMode === 'view' && (
                  <button
                    onClick={() => {
                      setShowAddUserModal(true);
                      setShowDetailsModal(false);
                    }}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: '#28a745',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '500'
                    }}
                  >
                    {t('users.addUser') || 'Add User'}
                  </button>
                )}
                {detailsViewMode === 'view' && (
                  <button
                    onClick={() => setDetailsViewMode('edit')}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: '#007acc',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '500'
                    }}
                  >
                    {t('common.edit') || 'Edit'}
                  </button>
                )}
                {detailsViewMode === 'edit' && (
                  <button
                    onClick={() => setDetailsViewMode('view')}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: 'transparent',
                      color: '#666',
                      border: '1px solid #ddd',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '500'
                    }}
                  >
                    {t('common.back') || 'Back'}
                  </button>
                )}
              </div>
            </div>

            {detailsViewMode === 'view' ? (
              /* View Mode - Show group details */
              <div>
                {/* Get all members for this group */}
                {(() => {
                  const allMembers = allUsers.filter(u => u.groupId === selectedGroup?.id);
                  const managers = editData.managers || [];
                  const regularMembers = allMembers.filter(u => !managers.includes(u.id));
                  
                  return (
                    <>
                      {/* Managers Section */}
                      <div style={{ marginBottom: '30px' }}>
                        <h4 style={{ marginBottom: '20px', color: '#333' }}>
                          {t('groups.managers') || 'Managers'}
                        </h4>
                        {managers.length === 0 ? (
                          <div style={{ 
                            textAlign: 'center', 
                            padding: '40px', 
                            border: '1px solid #e9ecef', 
                            borderRadius: '8px',
                            backgroundColor: '#f8f9fa'
                          }}>
                            <div style={{ fontSize: '32px', marginBottom: '12px' }}>👥</div>
                            <p style={{ margin: 0, color: '#666' }}>{t('members.noMembers') || 'No managers'}</p>
                          </div>
                        ) : (
                          <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
                            gap: '16px'
                          }}>
                            {managers.map((managerId) => {
                              const member = allUsers.find(u => u.id === managerId);
                              if (!member) return null;
                              
                              return (
                                <div
                                  key={managerId}
                                  style={{
                                    border: '1px solid #e9ecef',
                                    borderRadius: '8px',
                                    padding: '16px',
                                    backgroundColor: 'white',
                                    position: 'relative'
                                  }}
                                >
                                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                                    <div
                                      style={{
                                        width: '40px',
                                        height: '40px',
                                        backgroundColor: '#f8f9fa',
                                        border: '1px solid #e9ecef',
                                        borderRadius: '50%',
                                        marginRight: '12px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: '18px',
                                        fontWeight: '600',
                                        color: '#6c757d'
                                      }}
                                    >
                                      {member.name.charAt(0).toUpperCase()}
                                    </div>
                                    <div style={{ flex: 1 }}>
                                      <h4 style={{ margin: '0 0 4px 0', fontSize: '16px', fontWeight: '600', color: '#333' }}>
                                        {member.name}
                                      </h4>
                                      <p style={{ margin: 0, fontSize: '14px', color: '#666' }}>
                                        {member.email}
                                      </p>
                                    </div>
                                    {member.id !== user.id && (
                                      <button
                                        onClick={() => handleRemoveDetailsManager(member.id)}
                                        style={{
                                          position: 'absolute',
                                          top: '12px',
                                          ...(i18n.language === 'he' ? { left: '12px' } : { right: '12px' }),
                                          background: 'none',
                                          border: 'none',
                                          color: '#dc3545',
                                          cursor: 'pointer',
                                          fontSize: '20px',
                                          width: '24px',
                                          height: '24px',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          borderRadius: '50%',
                                          transition: 'all 0.2s ease'
                                        }}
                                        onMouseEnter={(e) => {
                                          e.currentTarget.style.backgroundColor = '#fee';
                                          e.currentTarget.style.transform = 'scale(1.1)';
                                        }}
                                        onMouseLeave={(e) => {
                                          e.currentTarget.style.backgroundColor = 'transparent';
                                          e.currentTarget.style.transform = 'scale(1)';
                                        }}
                                        title={t('groups.removeManager') || 'Remove Manager'}
                                      >
                                        ×
                                      </button>
                                    )}
                                  </div>
                                  <div style={{ fontSize: '12px', color: '#666', marginTop: '8px' }}>
                                    <div style={{ marginBottom: '4px' }}>
                                      <strong>{t('members.role') || 'Role'}:</strong> {t('groups.manager') || 'Manager'}
                                    </div>
                                    {member.lastLoginAt && (
                                      <div>
                                        <strong>{t('members.lastLogin') || 'Last Login'}:</strong> {
                                          typeof member.lastLoginAt === 'string'
                                            ? new Date(member.lastLoginAt).toLocaleDateString()
                                            : t('members.never') || 'Never'
                                        }
                                      </div>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>

                      {/* Regular Members Section */}
                      <div>
                        <h4 style={{ marginBottom: '20px', color: '#333' }}>
                          {t('groups.members') || 'Members'}
                        </h4>
                        {regularMembers.length === 0 ? (
                          <div style={{ 
                            textAlign: 'center', 
                            padding: '40px', 
                            border: '1px solid #e9ecef', 
                            borderRadius: '8px',
                            backgroundColor: '#f8f9fa'
                          }}>
                            <div style={{ fontSize: '32px', marginBottom: '12px' }}>👥</div>
                            <p style={{ margin: 0, color: '#666' }}>{t('members.noMembers') || 'No members'}</p>
                          </div>
                        ) : (
                          <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
                            gap: '16px'
                          }}>
                            {regularMembers.map((member) => {
                              return (
                                <div
                                  key={member.id}
                                  style={{
                                    border: '1px solid #e9ecef',
                                    borderRadius: '8px',
                                    padding: '16px',
                                    backgroundColor: 'white',
                                    position: 'relative'
                                  }}
                                >
                                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                                    <div
                                      style={{
                                        width: '40px',
                                        height: '40px',
                                        backgroundColor: '#f8f9fa',
                                        border: '1px solid #e9ecef',
                                        borderRadius: '50%',
                                        marginRight: '12px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: '18px',
                                        fontWeight: '600',
                                        color: '#6c757d'
                                      }}
                                    >
                                      {member.name.charAt(0).toUpperCase()}
                                    </div>
                                    <div style={{ flex: 1 }}>
                                      <h4 style={{ margin: '0 0 4px 0', fontSize: '16px', fontWeight: '600', color: '#333' }}>
                                        {member.name}
                                      </h4>
                                      <p style={{ margin: 0, fontSize: '14px', color: '#666' }}>
                                        {member.email}
                                      </p>
                                    </div>
                                    {member.id !== user.id && (
                                      <button
                                        onClick={() => handleRemoveMember(member.id)}
                                        style={{
                                          position: 'absolute',
                                          top: '12px',
                                          ...(i18n.language === 'he' ? { left: '12px' } : { right: '12px' }),
                                          background: 'none',
                                          border: 'none',
                                          color: '#dc3545',
                                          cursor: 'pointer',
                                          fontSize: '20px',
                                          width: '24px',
                                          height: '24px',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          borderRadius: '50%',
                                          transition: 'all 0.2s ease'
                                        }}
                                        onMouseEnter={(e) => {
                                          e.currentTarget.style.backgroundColor = '#fee';
                                          e.currentTarget.style.transform = 'scale(1.1)';
                                        }}
                                        onMouseLeave={(e) => {
                                          e.currentTarget.style.backgroundColor = 'transparent';
                                          e.currentTarget.style.transform = 'scale(1)';
                                        }}
                                        title={t('groups.removeMember') || 'Remove Member'}
                                      >
                                        ×
                                      </button>
                                    )}
                                  </div>
                                  <div style={{ fontSize: '12px', color: '#666', marginTop: '8px' }}>
                                    <div style={{ marginBottom: '4px' }}>
                                      <strong>{t('members.role') || 'Role'}:</strong> {member.organizationRole || t('users.roles.social_worker') || 'Member'}
                                    </div>
                                    {member.lastLoginAt && (
                                      <div>
                                        <strong>{t('members.lastLogin') || 'Last Login'}:</strong> {
                                          typeof member.lastLoginAt === 'string'
                                            ? new Date(member.lastLoginAt).toLocaleDateString()
                                            : t('members.never') || 'Never'
                                        }
                                      </div>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </>
                  );
                })()}
              </div>
            ) : (
              /* Edit Mode - Show form fields */
              <>
            {/* Group Name */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                {t('groups.name')} *
              </label>
              <input
                type="text"
                value={editData.name}
                onChange={(e) => setEditData(prev => ({ ...prev, name: e.target.value }))}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #ddd',
                  borderRadius: '6px',
                  fontSize: '14px'
                }}
              />
            </div>

            {/* Description */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                {t('groups.description')}:
              </label>
              <textarea
                value={editData.description}
                onChange={(e) => setEditData(prev => ({ ...prev, description: e.target.value }))}
                placeholder={t('groups.enterDescription')}
                rows={3}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #ddd',
                  borderRadius: '6px',
                  fontSize: '14px',
                  resize: 'vertical'
                }}
              />
            </div>

            {/* Logo */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                {t('groups.logo')}
              </label>
              <input
                type="file"
                accept="image/jpeg,image/jpg,.jpg,.jpeg"
                onChange={handleDetailsLogoUpload}
                disabled={isUploading}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #ddd',
                  borderRadius: '6px',
                  fontSize: '14px'
                }}
              />
              {isUploading && (
                <div style={{ marginTop: '8px', color: '#666', fontSize: '14px' }}>
                  {t('groups.wizard.uploading')}...
                </div>
              )}
              {editData.logoUrl && (
                <div style={{ marginTop: '12px' }}>
                  <img
                    key={editData.logoUrl}
                    src={editData.logoUrl}
                    alt="Group logo"
                    style={{
                      maxWidth: '200px',
                      maxHeight: '100px',
                      border: '1px solid #ddd',
                      borderRadius: '6px'
                    }}
                    onLoad={() => setIsUploading(false)}
                  />
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '30px' }}>
              <button
                onClick={() => handleDeleteGroup(selectedGroup.id)}
                disabled={isDeleting}
                style={{
                  padding: '12px 24px',
                  border: 'none',
                  borderRadius: '6px',
                  background: '#dc3545',
                  color: 'white',
                  cursor: isDeleting ? 'not-allowed' : 'pointer',
                  fontSize: '16px',
                  opacity: isDeleting ? 0.6 : 1
                }}
              >
                {isDeleting ? t('groups.deleting') : t('groups.delete')}
              </button>
              <button
                onClick={handleUpdateGroup}
                disabled={isUpdating || !editData.name.trim()}
                style={{
                  padding: '12px 24px',
                  border: 'none',
                  borderRadius: '6px',
                  backgroundColor: '#007acc',
                  color: 'white',
                  cursor: isUpdating || !editData.name.trim() ? 'not-allowed' : 'pointer',
                  fontSize: '16px',
                  opacity: isUpdating || !editData.name.trim() ? 0.6 : 1
                }}
              >
                {isUpdating ? t('groups.updating') : t('groups.update')}
              </button>
            </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Add User Modal */}
      {selectedGroup && (
        <AddUserModal
          user={user}
          isOpen={showAddUserModal}
          onClose={async () => {
            setShowAddUserModal(false);
            // Refresh the user list when modal closes
            try {
              const usersRef = collection(db, 'users');
              const snapshot = await getDocs(usersRef);
              const users: UserManagementUser[] = [];
              
              snapshot.forEach((doc) => {
                const userData = doc.data();
                if (!userData.deleted) {
                  users.push({
                    id: doc.id,
                    name: userData.name || '',
                    email: userData.email || '',
                    role: userData.role,
                    organizationId: userData.organizationId,
                    organizationRole: userData.organizationRole,
                    groupId: userData.groupId,
                    programId: userData.programId,
                    createdAt: userData.createdAt?.toDate?.()?.toISOString() || '',
                    lastLoginAt: userData.lastLoginAt?.toDate?.()?.toISOString() || '',
                    loginCount: userData.loginCount || 0,
                    blocked: userData.blocked || false
                  });
                }
              });
              setAllUsers(users);
            } catch (error) {
              console.error('Error loading users:', error);
            }
          }}
          organizationId={selectedGroup.organizationId}
          programId={selectedGroup.programId}
          groupId={selectedGroup.id}
          entityType="group"
          entityName={selectedGroup.name}
        />
      )}
    </div>
  );
}
